import { useState, useLayoutEffect, useRef, useMemo } from 'preact/hooks';
import { Signal } from "@preact/signals";
import { FlashCard, FlashCardReview, friendlyTimeDelta, getNextReviewDate, REVIEW_RESULTS, ReviewResult } from '../lib/flash-card-utils';
import { store, optionsStore } from '../lib/flash-card-store';
import { Tooltip } from './Tippy';

interface DefaultConfig {
  collapsed: boolean;
}

type CardStatus = 'waiting' | 'ready' | 'congrats';

type IconKind = ReviewResult | CardStatus;

interface ReviewIconProps {
  kind: IconKind;
  timestamp: Date;
}

const ICON_COLOR_MAP: Map<IconKind, string> = new Map([
  ['again', 'bg-red-500'],
  ['good', 'bg-green-500'],
  ['waiting', 'bg-blue-500'],
  ['ready', 'bg-yellow-500'],
  ['congrats', 'bg-pink-500'],
]);


function ReviewIcon({ kind, timestamp }: ReviewIconProps) {
  const colors = ICON_COLOR_MAP.get(kind);
  const displayKind = kind[0].toUpperCase() + kind.slice(1);
  const timeDelta = friendlyTimeDelta(timestamp);
  const timeStr = timestamp.toLocaleString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  });
  const tooltip = kind == "congrats" ?
    "Hooray!" :
    REVIEW_RESULTS.has(kind as ReviewResult) ?
      `${displayKind} — ${timeDelta}\n(${timeStr})` :
      kind == 'waiting' ?
        `${displayKind} — ready ${timeDelta}\n(${timeStr})` :
        `${displayKind}!`; // ready
  return (
    <Tooltip content={tooltip}>
      <div class="relative h-full w-5">
        <div class={`w-2 h-2 ${colors} rounded-full absolute top-1/2 left-1/2 blur-xs -translate-y-1 -translate-x-1`}></div>
        <div class={`w-2 h-2 ${colors} rounded-full absolute top-1/2 left-1/2 blur-[2px] -translate-y-1 -translate-x-1`}></div>
        <div class={`w-2 h-2 ${colors} rounded-full absolute top-1/2 left-1/2 -translate-y-1 -translate-x-1`}></div>
      </div >
    </Tooltip>
  )
}

/**
 * passed area -> deck
 */
interface DeckMetadata {
  mode: 'review' | 'read' | 'mix';
  prefix: string;
  nReady: number;
  nFiltered: number;
}

/**
 * passed deck -> card for display in top menu bar
 */
interface BarMetadata extends DeckMetadata {
  index: number;
}

interface FlashCardMenuProps {
  id: string;
  collapsed: boolean;
  setCollapsed: (c: boolean) => void;
  onToggleOptions: () => void;
  reviews: FlashCardReview[];
  status: CardStatus;
  due: Date;
  metadata: BarMetadata;
}

function FlashCardMenu({ id, collapsed, setCollapsed, onToggleOptions, reviews, status, due, metadata }: FlashCardMenuProps) {
  // bar hover class styles: Visible by default (mobile), hide and wait for hover on larger screens
  const bh = 'opacity-100 sm:opacity-0 sm:group-hover/bar:opacity-100 transition-opacity duration-150'

  // tooltip: explained card count
  const ccTooltip = metadata.mode == "review" ?
    `[Reviewing: ${metadata.nReady} ready]` :
    metadata.mode == "read" ?
      `(Reading: ${metadata.index + 1}/${metadata.nFiltered})` :
      `{Mix mode: ${metadata.index + 1}/${metadata.nFiltered} [${metadata.nReady} ready to review]}`;

  // rendered in bar: minimal
  const ccBar = metadata.mode == "review" ?
    `[${metadata.nReady}]` :
    metadata.mode == "read" ?
      `(${metadata.index + 1}/${metadata.nFiltered})` :
      `{${metadata.index + 1}/${metadata.nFiltered}}`;

  // split id into [prefix, suffix] to render differently
  const suffix = id.slice(metadata.prefix.length);

  return (
    // overall bar
    <div class={`${!collapsed && "border-b"} h-8 text-sm flex`}>
      {/* collapse control */}
      <div class="border-r w-8 h-full px-2 grid place-items-center hover:bg-zinc-950 hover:border-r-zinc-950 hover:text-zinc-50 hover:dark:bg-zinc-50 hover:dark:border-r-zinc-50 hover:dark:text-zinc-950 hover:vaporwave:bg-zinc-50 hover:vaporwave:text-zinc-950 hover:cursor-pointer group/collapse select-none" onClick={() => setCollapsed(!collapsed)}>
        {collapsed ?
          <><span class="group-hover/collapse:hidden">▷</span><span class="hidden group-hover/collapse:block">▶</span></> :
          <><span class="group-hover/collapse:hidden">▽</span><span class="hidden group-hover/collapse:block">▼</span></>
        }
      </div>

      <div class="grow flex h-full group/bar justify-between">
        {/* left side */}
        <div class={`${bh} flex`}>
          {/* show global options button */}
          <div class={`w-8 h-full px-2 grid place-items-center hover:opacity-50 hover:cursor-pointer select-none text-2xl`} onClick={onToggleOptions}>⚙</div>
        </div>

        {/* center */}
        <div class={`h-full ${bh} flex`}>
          {/* show [x ready] or (reading x/y), unless 'congrats' screen */}
          {status != 'congrats' && (
            <div class="grid place-items-center">
              <Tooltip content={ccTooltip}>
                <span class="font-mono text-xs mr-2 sm:mr-6 text-zinc-600  dark:text-zinc-300 vaporwave:text-white/70">{ccBar}</span>
              </Tooltip>
            </div>
          )}
          {/* show prefix/suffix, with prefix bolded */}
          <div class="grid place-items-center">
            <Tooltip content={`Filter: ${metadata.prefix}`}>
              <span class="font-mono text-xs"><span class="font-bold">{metadata.prefix}</span>{suffix}</span>
            </Tooltip>
          </div>
        </div>


        {/* right side */}
        {/* review icons */}
        <div class="flex mr-2">
          {/* history (omit entirely on mobile) */}
          <div class={`hidden sm:flex ${bh}`}>
            {reviews.map((review) => <ReviewIcon key={review.date + '-' + review.result} kind={review.result} timestamp={review.date} />)}
          </div>
          {/* current */}
          <ReviewIcon kind={status} timestamp={due} />
        </div>
      </div>
    </div>
  );
}

type Side = 'front' | 'back';

interface FlashCardBodyProps {
  prompt: string;
  answer: string;
  side: Side;
}


function FlashCardBody({ prompt, answer, side }: FlashCardBodyProps) {
  const cardBodyRef = useRef<HTMLDivElement>(null);

  // hook to run MathJax when state changes. needed because MathJax just runs
  // once on page load, but if we change what we're rendering with Preact, we'll
  // be adding new DOM content, which MathJax won't have seen! The visibility
  // hacks are to prevent the flash. Note that there is still a flash on page
  // load. This is intentional because it exists throughout the site, and I
  // don't (yet) want to try to intercept MathJax and fix it globally.
  useLayoutEffect(() => {
    const el = cardBodyRef.current;
    if (!el) return;

    // 1. Hide content immediately (before browser paint)
    el.style.visibility = 'hidden';

    // 2. Simple Guard: Is MathJax ready?
    const mj = (window as any).MathJax;

    if (mj && mj.typesetPromise) {
      // 3. Render and Reveal
      mj.typesetPromise([el]).then(() => {
        el.style.visibility = 'visible';
      });
    } else {
      // 4. Fallback: MathJax isn't ready yet (initial load race condition).
      // Just show the content immediately so it's not invisible.
      // (The global auto-render will likely catch this shortly after)
      el.style.visibility = 'visible';
    }

  }, [prompt, answer, side]); // Re-run on any content change


  return (
    <div ref={cardBodyRef} class={`p-8 sm:p-16 min-h-64 sm:min-h-96 [&_p,&_ul,&_ol,&_img]:my-0 [&_ul,&_ol]:pl-8`}>
      <div
        class={`${side == "front" ? "text-xl sm:text-3xl" : "text-base sm:text-lg"}`}
        dangerouslySetInnerHTML={{ __html: prompt }}
      />
      {side == "back" && (
        <>
          <hr class="my-8" />
          <div
            class="text-xl sm:text-2xl"
            dangerouslySetInnerHTML={{ __html: answer }}
          />
        </>
      )}
    </div>
  );
}

interface FlashCardActionsProps {
  side: Side;
  setSide: (s: Side) => void;
  status: CardStatus;
  onReview: (result: ReviewResult) => void;
  onSkip: () => void;
  doFinishedAck: () => void;
}

function FlashCardActions({ side, setSide, status, onReview, onSkip, doFinishedAck }: FlashCardActionsProps) {
  // hs: hover style
  const hs = "hover:bg-zinc-950 hover:text-zinc-50 hover:dark:bg-zinc-50 hover:dark:text-zinc-950 hover:vaporwave:bg-zinc-50 hover:vaporwave:text-zinc-950 hover:cursor-pointer select-none"
  const skipText = status == "ready" ? "skip" : "next";

  return (
    status == "congrats" ? (
      <div class="flex border-t text-base uppercase h-12 tracking-wide font-sans">
        <div class={`w-full grid place-items-center ${hs}`} onClick={doFinishedAck}>♧ reading mode</div>
      </div>
    ) : side == 'front' ? (
      <div class="flex border-t text-base uppercase h-12 tracking-wide font-sans">
        <div class={`w-4/5 border-r grid place-items-center ${hs}`} onClick={() => setSide('back')}>⤿ flip</div>
        <div class={`w-1/5 grid place-items-center ${hs}`} onClick={() => onSkip()}>» {skipText}</div>
      </div>
    ) : (
      <div class="flex border-t text-base uppercase h-12 tracking-wide font-sans">
        {status == "ready" ? (<>
          <div class={`w-2/5 border-r grid place-items-center ${hs}`} onClick={() => onReview('again')}>✗ again</div>
          <div class={`w-2/5 border-r grid place-items-center ${hs}`} onClick={() => onReview('good')}>✓ good</div>
        </>) :
          <div class={`w-4/5 border-r`}></div>
        }
        <div class={`w-1/5 grid place-items-center ${hs}`} onClick={() => onSkip()}>» {skipText}</div>
      </div>
    )
  );
}

interface FlashCardProps {
  card: FlashCard;
  due: Date;
  onToggleOptions: () => void;
  onReview: (result: ReviewResult) => void;
  onSkip: () => void;
  defaultConfig: DefaultConfig;
  metadata: BarMetadata;
  // the following are all for the special 'congrats' card
  doFinishedAck: () => void;
  special?: 'congrats';
  startSide?: Side;
}

export function FlashCard({ card, due, onToggleOptions, onReview, onSkip, doFinishedAck, defaultConfig, metadata, special, startSide }: FlashCardProps) {
  const { id, prompt, answer, reviews } = card;
  const [collapsed, setCollapsed] = useState(defaultConfig.collapsed)
  const [side, setSide] = useState<Side>(startSide ?? 'front')
  const status: CardStatus = special == "congrats" ? 'congrats' : due < new Date() ? 'ready' : 'waiting';
  const onSkipComplete = () => {
    onSkip();
    setSide('front');
  }

  return (
    <div class="border">
      <FlashCardMenu id={id} collapsed={collapsed} setCollapsed={setCollapsed} onToggleOptions={onToggleOptions} reviews={reviews} status={status} due={due} metadata={metadata} />

      {!collapsed &&
        <>
          <FlashCardBody key={id} prompt={prompt} answer={answer} side={side} />
          <FlashCardActions side={side} setSide={setSide} status={status} onReview={onReview} onSkip={onSkipComplete} doFinishedAck={doFinishedAck} />
        </>
      }
    </div>
  );
}

interface CongratsCardProps {
  prefix: string;
  nFiltered: number;
  onToggleOptions: () => void;
  doFinishedAck: () => void;
  reviewTimestamps: Date[];
}

/**
 * Special "card" that renders only as an intermediary when reviews are
 * finished, and before entering reading mode.
 *
 * Rendered as an alternative to a FlashCardDeck.
 */
export function CongratsCard({ prefix, nFiltered, onToggleOptions, doFinishedAck, reviewTimestamps }: CongratsCardProps) {
  return (<FlashCard
    card={{
      id: prefix,
      prompt: "Congrats!",
      answer: `Reviewed ${reviewTimestamps.length} cards. Use reading mode to enjoy the library of all ${nFiltered} cards here.`,
      reviews: [],
    }}
    due={new Date()}
    onToggleOptions={onToggleOptions}
    onReview={(result: ReviewResult) => { }}
    onSkip={() => { }}
    doFinishedAck={doFinishedAck}
    defaultConfig={{ collapsed: false }}
    metadata={{ nReady: 0, nFiltered: 0, index: 0, prefix: prefix, mode: 'review' }}
    special='congrats'
    startSide='back'
  />)
}


interface FlashCardOptionsProps {
}


export function FlashCardOptions({ }: FlashCardOptionsProps) {
  return (
    <div class="mb-4 border p-4 border-dashed">
      <p class="text-base font-bold my-2 font-sans">Flash Card Options</p>
      <table class="my-0">
        {/* Good practice to wrap table rows in tbody */}
        <tbody class="[&_td]:p-2">
          {/* Option 1: Mix Mode */}
          <tr>
            <th>Option</th>
            <th>Value</th>
            <th>Desc</th>
          </tr>
          <tr>
            <td class="font-mono text-sm">
              mix mode
            </td>
            <td>
              <input
                type='checkbox'
                checked={optionsStore.mixMode}
                onChange={(e) => {
                  // The setter on your class handles Signal update + DB persist
                  optionsStore.mixMode = e.currentTarget.checked;
                }}
              />
            </td>
            <td>
              Whether to show all cards (both ready and waiting) for a flash card area at once.
            </td>
          </tr>

          {/* Option 2: Default Collapsed */}
          <tr>
            <td class="font-mono text-sm">
              default collapsed
            </td>
            <td>
              <input
                type='checkbox'
                checked={optionsStore.defaultCollapsed}
                onChange={(e) => {
                  optionsStore.defaultCollapsed = e.currentTarget.checked;
                }}
              />
            </td>
            <td>
              Whether flash card areas start collapsed by default on page load.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

interface FlashCardAreaProps {
  /**
   * Filters to cards with this prefix
   */
  prefix: string;
}

interface FlashCardDeckProps {
  cards: Signal<FlashCard>[];
  /**
   * Track reviews for congrats card
   */
  appendSessionReview: () => void;
  onToggleOptions: () => void;
  metadata: DeckMetadata;
}

/**
 * Extremely lightweight wrapper around a collection of cards just to support
 * 'skipping'.
 *
 * Brief: the 'review' set and 'reading' set are different Decks.
 *
 * Longer: When you skip around in a review queue, this skipping should reset
 * when you clear the review queue and enter reading mode for the full filtered
 * set. We handle this by wrapping each set of cards in its own Deck, and each
 * Deck stores its own independent skipCount.
 */
function FlashCardDeck({ cards, onToggleOptions, appendSessionReview, metadata }: FlashCardDeckProps) {
  // Monotonic counter to track skips/next actions
  const [skipCount, setSkipCount] = useState(0);

  const n = cards.length;
  // Use modulo to safely wrap around or handle shrinking lists (reviews)
  // This ensures index is always valid [0, n-1] even if cards are removed.
  const index = skipCount % n;

  const cardSignal = cards[index];
  const card = cardSignal.value;

  return (
    <FlashCard
      key={card.id}
      card={card}
      due={getNextReviewDate(card.reviews)}
      onToggleOptions={onToggleOptions}
      defaultConfig={{ collapsed: optionsStore.defaultCollapsed }}
      onReview={(result: ReviewResult) => {
        appendSessionReview(); // track reviews for congrats card
        store.submitReview(card.id, result);
        // if reviewing in mix mode, we have to manually advance, because card
        // isn't removed from deck.
        if (metadata.mode == "mix") {
          setSkipCount(c => c + 1);
        }
      }}
      onSkip={() => { setSkipCount(c => c + 1) }}
      doFinishedAck={() => { }}
      metadata={{ ...metadata, index }}
    />
  );
}

export function FlashCardArea({ prefix }: FlashCardAreaProps) {
  const filteredCardsSignal = useMemo(() => store.getCardsByPrefix(prefix), [prefix]);
  const [reviewTimestamps, setReviewTimestamps] = useState<Date[]>([]);
  const [finishedAck, setFinishedAck] = useState(false);
  const [showOptions, setShowOptions] = useState(false);

  const appendSessionReview = () => { setReviewTimestamps([...reviewTimestamps, new Date()]) }
  const doFinishedAck = () => setFinishedAck(true);
  const onToggleOptions = () => setShowOptions(!showOptions);

  // Read .value to subscribe. If the list of cards changes (rare), this re-renders.
  const filteredCards = filteredCardsSignal.value;
  const now = new Date();
  // .value in loop here subscribes to updates to all filtered cards
  // NOTE: Inefficient to compute here and below, could save on bigger card data structure.
  const readyCards = filteredCards.filter((s) => getNextReviewDate(s.value.reviews) < now);
  const nFiltered = filteredCards.length;
  const nReady = readyCards.length;

  if (nFiltered == 0) {
    return <div>No cards found for section: {prefix}</div>;
  }

  // TODO: global 'mix mode' config to always show all (filtered) cards
  // Determine mode: (1) Congrats (done review, await ack -> read), (2) Reviewing due cards, (3) Reading all cards
  const mode = (nReady == 0 && reviewTimestamps.length > 0 && !finishedAck) ?
    'congrats' :
    optionsStore.mixMode ?
      'mix' :
      nReady > 0 ?
        'review' :
        'read';
  const cardSet = mode == 'congrats' ? [] : mode == 'review' ? readyCards : filteredCards;

  return (
    <div class="my-figure">
      {showOptions && <FlashCardOptions />}

      {mode == 'congrats' ? (
        <CongratsCard
          key={mode}
          nFiltered={nFiltered}
          prefix={prefix}
          onToggleOptions={onToggleOptions}
          reviewTimestamps={reviewTimestamps}
          doFinishedAck={doFinishedAck}
        />
      ) : (
        // Keying by mode ensures a fresh Deck (and reset skipCount)
        // when we switch from Reviewing -> Reading.
        <FlashCardDeck
          key={mode}
          cards={cardSet}
          onToggleOptions={onToggleOptions}
          appendSessionReview={appendSessionReview}
          metadata={{ prefix, nReady, nFiltered, mode }}
        />
      )}

      {/* render any problems we had in loading */}
      {store.problems.value.length > 0 && (
        <div class="px-8 py-4 bg-amber-200 text-amber-950">
          <p class="text-bold">Problems loading flash card questions:</p>
          <ul>
            {store.problems.value.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
